/**
 * Piezas de interfaz compartidas.
 *
 * Mantenerlas en un solo lugar evita que cada pantalla invente su propio boton
 * o su propia tarjeta, y que la app termine pareciendo cinco productos.
 */

import { useState } from "react";

import { cn } from "@turnpro/ui";
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  LabelHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";

/*
 * Se re-exporta en lugar de redefinirse: la implementacion vive en
 * `@turnpro/ui`, junto a los componentes que la usan, y de aca la siguen
 * importando las pantallas sin cambiar una linea.
 *
 * Va en dos pasos —importar arriba y reexportar aca— porque este archivo
 * tambien la usa, y un `export ... from` reexporta sin traer el nombre al
 * modulo.
 */
export { cn };

/* -------------------------------------------------------------------------- */
/* Botones                                                                     */
/* -------------------------------------------------------------------------- */

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "whatsapp" | "whatsappSoft";
type ButtonSize = "sm" | "md" | "lg";

const BUTTON_BASE =
  "inline-flex items-center justify-center gap-2 rounded-lg font-medium transition " +
  "disabled:cursor-not-allowed disabled:opacity-50";

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-brand-600 text-white shadow-sm hover:bg-brand-700 active:bg-brand-800",
  secondary:
    "border border-[var(--border-subtle)] bg-[var(--surface-raised)] hover:bg-[var(--surface-muted)]",
  ghost: "hover:bg-[var(--surface-muted)]",
  danger: "border border-red-300 text-red-700 hover:bg-red-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/40",
  /*
   * Lo que abre WhatsApp. El verde no es de la marca: es el de WhatsApp, y es
   * lo que hace que el boton se encuentre a simple vista entre los grises de
   * una agenda llena. Sale de la rampa `emerald` de Tailwind, la misma de los
   * estados "completado" y "confirmo que viene" — no es una paleta propia.
   *
   * Dos intensidades a proposito: el lleno para la accion principal de una
   * pantalla (uno por vez), el suave para los que se repiten en cada fila.
   * Veinte botones verdes llenos en una agenda dejarian de destacar todos.
   * El lleno es `emerald-700` en los dos temas: blanco sobre 600 no llega a
   * 4.5:1, y aclararlo en oscuro para que "brille" era justamente ese 600.
   */
  whatsapp:
    "bg-emerald-700 text-white shadow-sm hover:bg-emerald-800 active:bg-emerald-900",
  whatsappSoft:
    "border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 " +
    "dark:border-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-200 dark:hover:bg-emerald-500/20",
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-sm",
  md: "h-10 px-4 text-sm",
  lg: "h-11 px-5 text-base",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export function Button({
  variant = "primary",
  size = "md",
  className,
  type = "button",
  ...props
}: ButtonProps): ReactNode {
  return (
    <button
      type={type}
      className={cn(BUTTON_BASE, BUTTON_VARIANTS[variant], BUTTON_SIZES[size], className)}
      {...props}
    />
  );
}

/**
 * Un enlace con la forma de un boton.
 *
 * Existe para lo que sale de la app — abrir WhatsApp, la pagina publica del
 * negocio — donde un `<button>` con `onClick` seria mentirle al navegador: no
 * se podria abrir en otra pestaña ni copiar la direccion.
 */
export function ButtonLink({
  variant = "secondary",
  size = "md",
  className,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
}): ReactNode {
  return (
    <a
      className={cn(BUTTON_BASE, BUTTON_VARIANTS[variant], BUTTON_SIZES[size], className)}
      {...props}
    />
  );
}

/**
 * Un boton que pregunta antes de hacer algo que no se puede deshacer.
 *
 * Cancelar el turno de un cliente o marcarlo ausente estaban al lado de
 * "Completar", sin ninguna barrera: un dedo gordo en un telefono cancelaba una
 * reserva y no habia forma de volver atras. Del lado del cliente esto ya
 * estaba resuelto — la pantalla de su reserva pregunta antes de cancelar —,
 * asi que la misma cortesia va del lado del negocio.
 *
 * Es una pregunta en linea y no un dialogo: el panel no tiene ninguno, y uno
 * accesible de verdad (foco atrapado, cierre con Escape, fondo inerte) es
 * bastante mas de lo que este caso necesita.
 */
export function ConfirmButton({
  children,
  question,
  confirmLabel = "Si",
  onConfirm,
  disabled = false,
  variant = "danger",
  size = "sm",
}: {
  /** Lo que dice el boton en reposo. */
  children: ReactNode;
  /** Lo que se pregunta al apretarlo. */
  question: string;
  confirmLabel?: string;
  onConfirm: () => void;
  disabled?: boolean;
  variant?: ButtonVariant;
  size?: ButtonSize;
}): ReactNode {
  const [preguntando, setPreguntando] = useState(false);

  if (!preguntando) {
    return (
      <Button variant={variant} size={size} disabled={disabled} onClick={() => setPreguntando(true)}>
        {children}
      </Button>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span className="text-sm" role="status">
        {question}
      </span>
      <Button
        variant={variant}
        size={size}
        disabled={disabled}
        autoFocus
        onClick={() => {
          setPreguntando(false);
          onConfirm();
        }}
      >
        {confirmLabel}
      </Button>
      <Button variant="ghost" size={size} onClick={() => setPreguntando(false)}>
        No
      </Button>
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Contenedores                                                                */
/* -------------------------------------------------------------------------- */

export function Card({
  children,
  className,
  as: Tag = "div",
}: {
  children: ReactNode;
  className?: string;
  as?: "div" | "section" | "article" | "li";
}): ReactNode {
  return <Tag className={cn("surface-card", className)}>{children}</Tag>;
}

export function CardHeader({
  title,
  description,
  action,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}): ReactNode {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[var(--border-subtle)] px-5 py-4">
      <div>
        <h2 className="titulo font-semibold">{title}</h2>
        {description ? <p className="mt-0.5 text-sm text-muted">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}): ReactNode {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="titulo text-2xl font-semibold tracking-tight">{title}</h1>
        {description ? <p className="mt-1 text-muted">{description}</p> : null}
      </div>
      {action}
    </header>
  );
}

/* -------------------------------------------------------------------------- */
/* Formularios                                                                 */
/* -------------------------------------------------------------------------- */

const FIELD_CLASS =
  "w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--surface)] px-3 py-2 text-sm " +
  "placeholder:text-[var(--text-muted)] focus:border-brand-500";

export function Label({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>): ReactNode {
  return <label className={cn("block text-sm font-medium", className)} {...props} />;
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>): ReactNode {
  return <input className={cn(FIELD_CLASS, className)} {...props} />;
}

export function Textarea({
  className,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement>): ReactNode {
  return <textarea className={cn(FIELD_CLASS, className)} {...props} />;
}

export function Select({
  className,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>): ReactNode {
  return <select className={cn(FIELD_CLASS, "pr-8", className)} {...props} />;
}

export function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string | null;
  children: ReactNode;
}): ReactNode {
  return (
    <div>
      <Label htmlFor={htmlFor}>{label}</Label>
      <div className="mt-1.5">{children}</div>
      {error ? (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-muted">{hint}</p>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Estados de una reserva                                                      */
/* -------------------------------------------------------------------------- */

export const STATUS_LABELS: Record<string, string> = {
  pending: "A confirmar",
  confirmed: "Confirmado",
  completed: "Completado",
  cancelled: "Cancelado",
  no_show: "No asistio",
};

const STATUS_STYLES: Record<string, string> = {
  pending: "bg-amber-100 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200",
  confirmed: "bg-brand-100 text-brand-800 dark:bg-brand-500/15 dark:text-brand-200",
  completed: "bg-emerald-100 text-emerald-900 dark:bg-emerald-500/15 dark:text-emerald-200",
  cancelled: "bg-ink-200 text-ink-700 dark:bg-white/10 dark:text-ink-300",
  no_show: "bg-red-100 text-red-900 dark:bg-red-500/15 dark:text-red-200",
};

export function StatusBadge({ status }: { status: string }): ReactNode {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
        STATUS_STYLES[status] ?? STATUS_STYLES["cancelled"],
      )}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

export function Badge({ children }: { children: ReactNode }): ReactNode {
  return (
    <span className="inline-flex items-center rounded-full border border-[var(--border-subtle)] px-2.5 py-0.5 text-xs text-muted">
      {children}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Avisos                                                                      */
/* -------------------------------------------------------------------------- */

export function Alert({
  tone = "info",
  children,
}: {
  tone?: "info" | "warning" | "error" | "success";
  children: ReactNode;
}): ReactNode {
  const tones = {
    info: "border-brand-200 bg-brand-50 text-brand-900 dark:border-brand-900 dark:bg-brand-500/10 dark:text-brand-100",
    // Ambar: algo que hay que atender pero todavia no rompio nada, como una
    // suscripcion vencida dentro de la tolerancia.
    warning:
      "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200",
    error:
      "border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200",
    success:
      "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200",
  };

  return (
    <div
      role={tone === "error" || tone === "warning" ? "alert" : "status"}
      className={cn("rounded-lg border px-4 py-3 text-sm", tones[tone])}
    >
      {children}
    </div>
  );
}
